import { useEffect, useRef, useState } from "react";
import { Bot, Send } from "lucide-react";
import { API, api } from "../lib/api";
import { PageHeader } from "../components/Common";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const QUICK = [
  "Draft a friendly SMS follow-up for a lead who missed their consult",
  "Write a landing page headline and 3 bullet points for a counselling practice",
  "Summarise best practice for SOAP case notes",
  "Audit checklist: Australian Privacy Principles for a small business CRM",
  "Draft an NDIS reportable incident summary template",
];

export default function AIAssistant() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { api.get("/ai/history").then((r) => setMsgs(r.data)); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (prompt) => {
    if (!prompt.trim() || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: prompt }, { role: "assistant", text: "" }]);
    const res = await fetch(`${API}/ai/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("bos_token")}`, "X-Account-Id": localStorage.getItem("bos_account") || "" },
      body: JSON.stringify({ prompt }),
    });
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const parts = buf.split("\n\n");
      buf = parts.pop();
      for (const p of parts) {
        const d = p.replace(/^data: /, "");
        if (d === "[DONE]") continue;
        try {
          const j = JSON.parse(d);
          const add = j.t || (j.error ? `\n${j.error}` : "");
          setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + add }; return c; });
        } catch { /* partial chunk */ }
      }
    }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader overline="Copilot" title="BOS AI Assistant" desc="Draft replies, funnel copy, case summaries and compliance checks — tuned for Australian businesses." />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-3 space-y-2">
          <div className="overline mb-2">Quick actions</div>
          {QUICK.map((q, i) => <button key={q} onClick={() => ask(q)} data-testid={`ai-quick-${i}`} className="w-full text-left text-sm rounded-md border bg-card p-3 hover:border-primary/60 transition-colors">{q}</button>)}
        </div>
        <div className="lg:col-span-9 rounded-md border bg-card/60 flex flex-col h-[calc(100vh-15rem)] min-h-[480px]">
          <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ai-messages">
            {!msgs.length && <div className="m-auto text-center text-muted-foreground pt-20"><Bot className="h-10 w-10 mx-auto mb-3 text-primary" />Ask BOS AI anything about your business.</div>}
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-md p-3 text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-primary/20 border border-primary/30" : "bg-secondary"}`} data-testid={`ai-msg-${m.role}`}>
                {m.text || (busy && i === msgs.length - 1 ? "Thinking…" : "")}
              </div>
            ))}
            <div ref={end} />
          </div>
          <div className="p-3 border-t flex gap-2">
            <Textarea data-testid="ai-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask BOS AI…"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(text); } }} />
            <Button onClick={() => ask(text)} disabled={busy} data-testid="ai-send-btn"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
